import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StarRating from "../components/StarRating";
import { Button } from "../components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { Check, X, Trash2, Filter, MessageSquareQuote } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = [
    { value: "__all__", label: "Todas" },
    { value: "pending", label: "Em moderação" },
    { value: "approved", label: "Aprovadas" },
    { value: "rejected", label: "Rejeitadas" },
];

const STATUS_BADGE = {
    pending: { cls: "bg-amber-100 text-amber-900 border-amber-200", label: "Em moderação" },
    approved: { cls: "bg-green-100 text-green-900 border-green-200", label: "Aprovada" },
    rejected: { cls: "bg-zinc-100 text-zinc-700 border-zinc-200", label: "Rejeitada" },
};

function formatDateTime(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("pt-PT", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

export default function AdminReviewsPage() {
    const [reviews, setReviews] = useState([]);
    const [filter, setFilter] = useState("pending");
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const q = filter !== "__all__" ? `?status=${filter}` : "";
            const res = await api.get(`/admin/reviews${q}`);
            setReviews(res.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro");
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    async function moderate(r, status) {
        try {
            await api.put(`/admin/reviews/${r.id}`, { status });
            toast.success(status === "approved" ? "Avaliação aprovada." : "Avaliação rejeitada.");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro");
        }
    }

    async function remove(r) {
        if (!window.confirm("Eliminar definitivamente esta avaliação?")) return;
        try {
            await api.delete(`/admin/reviews/${r.id}`);
            toast.success("Avaliação eliminada.");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro");
        }
    }

    const counts = {
        pending: reviews.filter((r) => r.status === "pending").length,
    };

    return (
        <>
            <PageHeader
                eyebrow="Administração"
                title="Avaliações"
                description="Aprove ou rejeite avaliações enviadas pelos clientes antes de ficarem públicas."
                testid="admin-reviews-header"
                actions={
                    <div className="flex items-center gap-2">
                        <Filter className="h-4 w-4 text-zinc-400" />
                        <Select value={filter} onValueChange={setFilter}>
                            <SelectTrigger className="h-10 w-48 rounded-sm" data-testid="reviews-status-filter">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {STATUS_OPTIONS.map((s) => (
                                    <SelectItem key={s.value} value={s.value}>
                                        {s.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                }
            />

            {loading ? (
                <div className="flex items-center justify-center py-20">
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-300 border-t-[#FF3B30]" />
                </div>
            ) : reviews.length === 0 ? (
                <div className="border border-zinc-200 bg-white p-12 text-center" data-testid="admin-reviews-empty">
                    <MessageSquareQuote className="mx-auto mb-3 h-8 w-8 text-zinc-400" />
                    <h3 className="font-['Outfit'] text-lg font-semibold text-zinc-900">
                        Sem avaliações nesta categoria.
                    </h3>
                </div>
            ) : (
                <div className="space-y-4" data-testid="admin-reviews-list">
                    {reviews.map((r) => {
                        const badge = STATUS_BADGE[r.status] || STATUS_BADGE.pending;
                        return (
                            <article
                                key={r.id}
                                className="border border-zinc-200 bg-white p-5"
                                data-testid={`admin-review-${r.id}`}
                            >
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-zinc-900">
                                                {r.cliente_primeiro_nome}
                                            </span>
                                            <span className="text-xs text-zinc-500">·</span>
                                            <span className="text-xs text-zinc-600">
                                                {r.moto_label}
                                            </span>
                                            <span
                                                className={`ml-1 inline-flex items-center rounded-sm border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${badge.cls}`}
                                                data-testid={`admin-review-badge-${r.id}`}
                                            >
                                                {badge.label}
                                            </span>
                                        </div>
                                        <div className="mt-1 text-[11px] text-zinc-500">
                                            Submetida em {formatDateTime(r.created_at)}
                                            {r.reviewed_at
                                                ? ` · Moderada em ${formatDateTime(r.reviewed_at)}`
                                                : ""}
                                        </div>
                                    </div>
                                    <StarRating
                                        value={r.rating}
                                        readOnly
                                        size={18}
                                        testid={`admin-review-stars-${r.id}`}
                                    />
                                </div>

                                {r.comment ? (
                                    <p className="mt-3 text-sm leading-relaxed text-zinc-700">
                                        {r.comment}
                                    </p>
                                ) : (
                                    <p className="mt-3 text-sm italic text-zinc-400">
                                        Sem comentário escrito.
                                    </p>
                                )}

                                <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-zinc-100 pt-3">
                                    {r.status !== "approved" ? (
                                        <Button
                                            size="sm"
                                            onClick={() => moderate(r, "approved")}
                                            className="rounded-sm bg-green-700 hover:bg-green-800"
                                            data-testid={`admin-review-approve-${r.id}`}
                                        >
                                            <Check className="mr-1.5 h-4 w-4" /> Aprovar
                                        </Button>
                                    ) : null}
                                    {r.status !== "rejected" ? (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => moderate(r, "rejected")}
                                            className="rounded-sm"
                                            data-testid={`admin-review-reject-${r.id}`}
                                        >
                                            <X className="mr-1.5 h-4 w-4" /> Rejeitar
                                        </Button>
                                    ) : null}
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => remove(r)}
                                        className="rounded-sm text-red-600 hover:text-red-700"
                                        data-testid={`admin-review-delete-${r.id}`}
                                    >
                                        <Trash2 className="mr-1.5 h-4 w-4" /> Eliminar
                                    </Button>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}
        </>
    );
}
